import React, { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Quotation, CreateQuotationData, QuotationDocument, QuotationItem, QuotationStatus } from '../../types/quotation';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useCustomers } from '../../hooks/useCustomers';
import { useProducts } from '../../hooks/useProducts';
import { useQuotations } from '../../hooks/useQuotations';
import Modal from '../common/Modal';
import { DatePicker } from '../common/DatePicker';
import InputMask from 'react-input-mask';
import {
  BuildingOffice2Icon,
  UserIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  IdentificationIcon,
  AdjustmentsHorizontalIcon,
  DocumentIcon,
  XMarkIcon,
  PaperClipIcon,
  DocumentCheckIcon,
  DocumentChartBarIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { generateQuotationNumber } from '../../utils/formatters';
import { formatDocumentDate } from '../../utils/formatters';
import { Combobox, Portal } from '@headlessui/react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useStorage } from '../../hooks/useStorage';
import { Timestamp } from 'firebase/firestore';
import { Tooltip } from '../common/Tooltip';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import DocumentPreviewModal from '../preview/DocumentPreviewModal';
import { formatTaxNumber, formatRegistrationNumber, validateTaxNumber, validateRegistrationNumber } from '../../utils/validation';
import { toast } from 'react-hot-toast';

// Draft storage constants
const DRAFT_STORAGE_KEY = 'quotation_form_draft';
const DRAFT_TIMESTAMP_KEY = 'quotation_form_draft_timestamp';

interface QuotationFormProps {
  onSubmit: (data: CreateQuotationData) => void;
  onCancel: () => void;
  isOpen: boolean;
  initialData?: Quotation;
}

const QuotationForm: React.FC<QuotationFormProps> = ({ onSubmit, onCancel, isOpen, initialData }) => {
  const { t } = useLanguage();
  const { organizations } = useOrganizations();
  const { customers, loading: customersLoading } = useCustomers();
  const { quotations, refreshQuotations } = useQuotations();
  const { uploadFile, deleteFile } = useStorage();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedDocumentToEdit, setSelectedDocumentToEdit] = useState<any>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [orgQuery, setOrgQuery] = useState('');
  const [additionalPods, setAdditionalPods] = useState<number[]>([]);
  const [previewDocument, setPreviewDocument] = useState<any>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  
  // Új ügyfél létrehozásához kapcsolódó állapotok
  const [isNewCustomerMode, setIsNewCustomerMode] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  
  // Draft recovery state
  const [hasDraft, setHasDraft] = useState(false);
  const [draftTimestamp, setDraftTimestamp] = useState<Date | null>(null);

  // Új state változó az űrlap kezdeti állapotának jelzésére
  const [isInitialCustomerSelection, setIsInitialCustomerSelection] = useState(true);

  const lastQuotationNumber = quotations
    .map(q => q.number)
    .sort()
    .reverse()[0];

  // Draft recovery functions
  const saveDraft = (data: any) => {
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(data));
      localStorage.setItem(DRAFT_TIMESTAMP_KEY, new Date().toISOString());
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
  };

  const loadDraft = () => {
    try {
      const draftData = localStorage.getItem(DRAFT_STORAGE_KEY);
      const timestamp = localStorage.getItem(DRAFT_TIMESTAMP_KEY);
      
      if (draftData && timestamp) {
        const parsedData = JSON.parse(draftData);
        setDraftTimestamp(new Date(timestamp));
        return parsedData;
      }
    } catch (error) {
      console.error('Failed to load draft:', error);
    }
    return null;
  };

  const clearDraft = () => {
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      localStorage.removeItem(DRAFT_TIMESTAMP_KEY);
      setHasDraft(false);
      setDraftTimestamp(null);
    } catch (error) {
      console.error('Failed to clear draft:', error);
    }
  };

  const restoreDraft = () => {
    const draftData = loadDraft();
    if (draftData) {
      console.log('Restoring quotation draft with number:', draftData.number);
      
      // Ensure both quotation number fields are preserved and in sync
      if (draftData.number && !draftData.quotationNumber) {
        draftData.quotationNumber = draftData.number;
      } else if (draftData.quotationNumber && !draftData.number) {
        draftData.number = draftData.quotationNumber;
      }
      
      // Ha a piszkozatban már van ajánlati szám (nem "Minta"), akkor jelezzük,
      // hogy ez már nem az első ügyfélválasztás, így nem generálunk új számot
      if ((draftData.number && !draftData.number.includes('-Minta')) || 
          (draftData.quotationNumber && !draftData.quotationNumber.includes('-Minta'))) {
        setIsInitialCustomerSelection(false);
        console.log('Restored draft has a valid quotation number, setting isInitialCustomerSelection to false');
      }
      
      reset(draftData);
      // Also restore additional PODs
      if (draftData.additionalPods && Array.isArray(draftData.additionalPods)) {
        const podIndexes = draftData.additionalPods.map((_: any, index: number) => index);
        setAdditionalPods(podIndexes);
      }
      setHasDraft(false);
      clearDraft();
    }
  };

  // Draft recovery banner component
  const DraftRecoveryBanner = () => {
    if (!hasDraft || !draftTimestamp) return null;
    
    // Format the date for display
    const formattedDate = new Intl.DateTimeFormat('hu-HU', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(draftTimestamp);
    
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center justify-between">
        <div className="flex items-center">
          <DocumentTextIcon className="h-5 w-5 text-amber-600 mr-3" />
          <div>
            <h3 className="text-sm font-medium text-amber-800">Mentett vázlat elérhető</h3>
            <p className="text-xs text-amber-700">
              {`Egy mentett vázlat elérhető (${formattedDate}). Szeretné visszaállítani?`}
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={restoreDraft}
            className="flex items-center px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded transition-colors"
          >
            <ArrowPathIcon className="h-3.5 w-3.5 mr-1" />
            Visszaállítás
          </button>
          <button
            type="button"
            onClick={clearDraft}
            className="flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
          >
            <XMarkIcon className="h-3.5 w-3.5 mr-1" />
            Elvetés
          </button>
        </div>
      </div>
    );
  };

  // Cég rövidített nevének előállítása
  const getCompanyShortName = (customerId: string): string => {
    if (!customerId) return 'Unknown';
    
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return 'Unknown';
    
    // Ha nem szervezet, vagy nincs cégnév, akkor próbáljunk a névből rövidítést csinálni
    if (customer.type !== 'organization' || !customer.companyName) {
      if (customer.type === 'individual' && customer.firstName && customer.lastName) {
        // Egyéni ügyfél esetén a vezetéknevek kezdőbetűi + keresztnév első 3 karaktere
        const lastInitial = customer.lastName.charAt(0).toUpperCase();
        const firstPart = customer.firstName.substring(0, 3);
        return lastInitial + firstPart;
      }
      // Even for unknown individuals, return something
      return 'Client'; 
    }
    
    // Vállalat nevéből kivonjuk a rövidítést (max 12 karakter)
    const companyName = customer.companyName;
    console.log('Original company name:', companyName);
    
    // Ha van Kft, Zrt, stb a névben, azt elhagyjuk
    const nameWithoutSuffix = companyName.replace(/\s+(kft|zrt|bt|nyrt|kkt|kht|rt|gmbh)\.?$/i, '');
    console.log('Company name without suffix:', nameWithoutSuffix);
    
    // Eltávolítjuk az & jelet a névből, hogy ne okozzon problémát az XML-ben
    const nameWithoutAmpersand = nameWithoutSuffix.replace(/&/g, '');
    console.log('Name without ampersand:', nameWithoutAmpersand);
    
    // Split words, preserve hyphens as separate words to capture parts like "Nagykőrösi"
    const words = nameWithoutAmpersand.split(/[\s-]+/);
    console.log('Words extracted:', words);
    console.log('Number of words:', words.length);
    
    let shortName: string;
    
    // Check for hyphenated name (special handling for similar companies with different locations)
    if (nameWithoutAmpersand.includes('-')) {
      // For hyphenated names (like Megatherm-Nagykőrösi), include more of the second part
      const parts = nameWithoutAmpersand.split('-');
      if (parts.length >= 2) {
        // Take first part's initial segment + more distinct characters from second part
        const firstPart = parts[0].trim();
        const secondPart = parts[1].trim();
        
        // Extract more from the second part to distinguish similar locations (Szolnok vs Csillaghegy)
        let firstPartChars = '';
        let secondPartChars = '';
        
        // For first part, take 3-4 characters (or all if shorter)
        if (firstPart.length <= 4) {
          firstPartChars = firstPart;
        } else {
          // For longer first parts, take first 4 chars
          firstPartChars = firstPart.substring(0, 4);
        }
        
        // For second part, take up to 6 characters, prioritizing the start but including some unique middle chars
        if (secondPart.length <= 6) {
          secondPartChars = secondPart;
        } else {
          // For longer second parts, take first 3 chars + 3 chars from middle/end
          const middle = Math.floor(secondPart.length / 2);
          secondPartChars = secondPart.substring(0, 3) + secondPart.substring(middle, middle + 3);
        }
        
        shortName = (firstPartChars + secondPartChars).substring(0, 12);
      } else {
        shortName = nameWithoutAmpersand.substring(0, 12);
      }
    }
    else if (words.length === 1) {
      // Ha csak egy szó, annak vesszük az első 12 karakterét
      shortName = words[0].substring(0, 12);
    } 
    else if (words.length === 2) {
      // Két szó esetén: első szó első 5 betűje + második szó első 6 karaktere
      const firstPart = words[0].substring(0, 5);
      const secondPart = words[1].substring(0, 6);
      shortName = (firstPart + secondPart).substring(0, 12);
    }
    else if (words.length >= 4) {
      console.log('Using 4+ word handling for:', nameWithoutAmpersand);
      // Hosszú, több szavas cégeknél (pl. Oerlikon Balzers Coating Austria GmbH)
      // Prioritást adunk az első két szónak, mert azok általában a fő cégnevet adják
      
      // Az első szó első 7 karaktere
      const firstWord = words[0].substring(0, 7);
      console.log('First word part:', firstWord);
      
      // A második szó első 5 karaktere
      const secondWord = words[1].substring(0, 5);
      console.log('Second word part:', secondWord);
      
      // Összeállítjuk a két szóból (pl. "OerlikoBalze")
      shortName = (firstWord + secondWord).substring(0, 12);
      console.log('Combined shortname from first two words:', shortName);
    }
    else {
      // Három szavas cégeknél
      // 1. Első szó első 3 karaktere
      const firstPart = words[0].substring(0, 3);
      
      // 2. Középső szavak - 2-3 karaktert kiemelünk belőlük
      let middlePart = '';
      if (words.length > 2) {
        middlePart = words.slice(1, -1).map(word => {
          if (word.length > 3) {
            // Hosszabb szavakból első 3 karakter
            return word.substring(0, 3);
          }
          return word.substring(0, 2); // Rövidebb szavakból 2 karakter
        }).join('');
      }
      
      // 3. Utolsó szó első 4 karaktere
      const lastPart = words[words.length - 1].substring(0, 4);
      
      // Összeállítjuk a rövidítést, de figyelünk a maximális hosszra (12 karakter)
      shortName = (firstPart + middlePart + lastPart).substring(0, 12);
    }
    
    // Ensure we have at least one character
    if (!shortName || shortName.length === 0) {
      shortName = 'Comp';
    }
    
    // Az első betűt nagybetűssé tesszük
    const result = shortName.charAt(0).toUpperCase() + shortName.slice(1);
    console.log('Generated short name:', result);
    return result;
  };

  const defaultValues: CreateQuotationData = {
    status: 'draft',
    number: generateQuotationNumber('', 'Minta', [], '', 'quotation', '', []),
    organizationId: '',
    customerId: '',
    quotationNumber: '',
    companyName: '',
    companyAddress: '',
    taxNumber: '',
    registrationNumber: '',
    representativeName: '', // Add the representativeName field
    contactName: '',
    date: '',
    validUntil: '',
    paymentDue: '15', // Alapértelmezett fizetési határidő 15 nap
    price: '',
    priceUnit: 'HUF/kWh',
    consumption: '',
    contractStart: '',
    contractEnd: '',
    kat: {
      kat: '',
      text: ''
    },
    pod: {
      id: '',
      address: '',
      postalCode: '',
      city: '',
      street: '',
      measurementType: '',
      meteringType: '', // AMR vagy SLP
      annualConsumption: '',
      power: '',
      profileNumber: ''
    },
    additionalPods: [{
      id: '',
      address: '',
      postalCode: '',
      city: '',
      street: '',
      measurementType: '',
      meteringType: '', // AMR vagy SLP
      annualConsumption: '',
      power: '',
      profileNumber: ''
    }],
    documents: [],
    items: [],
    publicToken: '',
    notes: ''
  };

  const formatDate = (date: string | Timestamp | undefined): string => {
    // Use the getDateString helper first to convert Timestamp to string
    const dateStr = getDateString(date);
    if (!dateStr) return '';
    
    try {
      // ISO datetime formátum kezelése (YYYY-MM-DDTHH:MM)
      if (dateStr.includes('T')) {
        const dateObj = new Date(dateStr);
        if (isNaN(dateObj.getTime())) return dateStr;
        
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        
        return `${year}.${month}.${day}. ${hours}:${minutes}`;
      }
      
      // YYYY-MM-DD formátum átalakítása YYYY.MM.DD. formátumra
      const isoDateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (isoDateMatch) {
        const [_, year, month, day] = isoDateMatch;
        return `${year}.${month}.${day}.`;
      }
      
      // YYYY/MM/DD formátum átalakítása YYYY.MM.DD. formátumra
      const slashDateMatch = dateStr.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
      if (slashDateMatch) {
        const [_, year, month, day] = slashDateMatch;
        return `${year}.${month}.${day}.`;
      }
      
      // YYYY.MM.DD formátum ellenőrzése - ha nincs a végén pont, hozzáadjuk
      const dotDateMatch = dateStr.match(/^(\d{4})\.(\d{2})\.(\d{2})$/);
      if (dotDateMatch) {
        return `${dateStr}.`;
      }
      
      // Ha már helyes formátumban van (YYYY.MM.DD.)
      if (/^\d{4}\.\d{2}\.\d{2}\.$/.test(dateStr)) {
        return dateStr;
      }
      
      // Egyéb esetben megpróbáljuk Date objektumként értelmezni
      try {
        const d = new Date(dateStr);
        if (!isNaN(d.getTime())) {
          return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}.`;
        }
      } catch (parseError) {
        console.error('Date parsing error:', parseError);
      }
      
      // Ha egyik formátum sem ismerhető fel, visszaadjuk az eredeti értéket
      return dateStr;
    } catch (error) {
      console.error('Invalid date format:', dateStr, error);
      return dateStr || '';
    }
  };

  // Reguláris kifejezések az adószám és cégjegyzékszám validáláshoz
  const taxNumberPattern = /^\d{8}-\d-\d{2}$/;  // pl. 12098934-2-13
  const registrationNumberPattern = /^\d{2}-\d{2}-\d{6}$/;  // pl. 13-09-071461

  const { register, handleSubmit, control, setValue, watch, formState: { errors }, reset, getValues } = useForm<CreateQuotationData>({
    defaultValues: initialData ? {
      ...initialData,
      date: initialData.date || formatDocumentDate(new Date())
    } : {
      ...defaultValues,
      date: formatDocumentDate(new Date())
    }
  });

  // Helper function to convert Timestamp to string safely
  const getDateString = (date: string | Timestamp | undefined): string => {
    if (!date) return '';
    if (date instanceof Timestamp) {
      const d = date.toDate();
      return d.toISOString().split('T')[0]; // Convert to YYYY-MM-DD
    }
    return date;
  };

  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      // Frissítsük az adatbázisból a quotations listát, hogy biztos a legfrissebb adatokkal dolgozzunk
      refreshQuotations()
        .then(() => {
          console.log('Successfully refreshed quotations data:', quotations.length);
        })
        .catch(error => {
          console.error('Error refreshing quotations data:', error);
        });
        
      // Check for existing draft when form opens
      if (!initialData) {
        const draftData = loadDraft();
        if (draftData) {
          setHasDraft(true);
        } else {
          setHasDraft(false);
        }
      } else {
        // If editing existing quotation, clear any drafts
        clearDraft();
      }

      // Initialize form data with proper date formatting
      const formattedData = initialData ? {
        ...initialData,
        // Format dates in Hungarian format
        date: formatDate(getDateString(initialData.date)),
        // Keep validUntil in original format for datetime-local input
        validUntil: initialData.validUntil || '',
        paymentDue: initialData.paymentDue || '15', 
        contractStart: formatDate(getDateString(initialData.contractStart)),
        contractEnd: formatDate(getDateString(initialData.contractEnd))
      } : defaultValues;

      reset(formattedData);
      setCustomerSearch(''); // Reset customer search
      setAdditionalPods([]); // Reset additional PODs

      // Ha van initialData és abban van additionalPods, akkor automatikusan beállítjuk
      if (initialData?.additionalPods && Array.isArray(initialData.additionalPods)) {
        const podIndexes = initialData.additionalPods.map((_: any, index: number) => index);
        setAdditionalPods(podIndexes);
      } else {
        setAdditionalPods([]);
      }
    }
  }, [isOpen, initialData, reset]);

  // Auto-save draft when form values change (only for new quotations)
  useEffect(() => {
    // Only save draft for new quotations
    if (isOpen && !initialData) {
      const currentValues = getValues();
      // Don't save empty forms
      if (currentValues.organizationId || currentValues.customerId || currentValues.companyName) {
        saveDraft(currentValues);
      }
    }
  }, [watch(), isOpen, initialData, getValues]);

  // Save draft when modal is closed without submitting
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isOpen && !initialData) {
        const currentValues = getValues();
        // Don't save empty forms
        if (currentValues.organizationId || currentValues.customerId || currentValues.companyName) {
          saveDraft(currentValues);
          // Standard message for old browsers
          e.preventDefault();
          e.returnValue = '';
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isOpen, initialData, getValues]);

  // Documents mező regisztrálása
  React.useEffect(() => {
    register('documents');
  }, [register]);

  const selectedOrgId = watch('organizationId');
  const selectedOrg = organizations.find(org => org.id === selectedOrgId);

  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      const searchStr = customer.type === 'organization'
        ? customer.companyName?.toLowerCase()
        : `${customer.firstName} ${customer.lastName}`.toLowerCase();
      
      const searchTermLower = customerQuery.toLowerCase();
      
      return searchStr?.includes(searchTermLower) ||
        // Email keresés
        customer.email?.toLowerCase().includes(searchTermLower) ||
        // Adószám és cégjegyzékszám keresés
        customer.taxNumber?.toLowerCase().includes(searchTermLower) ||
        customer.registrationNumber?.toLowerCase().includes(searchTermLower) ||
        // Cím elemek keresése
        customer.address?.street?.toLowerCase().includes(searchTermLower) ||
        customer.address?.city?.toLowerCase().includes(searchTermLower) ||
        customer.address?.postalCode?.toLowerCase().includes(searchTermLower) ||
        customer.address?.country?.toLowerCase().includes(searchTermLower) ||
        // Teljes cím keresése (ha van)
        customer.companyAddress?.toLowerCase().includes(searchTermLower);
    });
  }, [customers, customerQuery]);

  const filteredOrganizations = useMemo(() => {
    return organizations.filter(org =>
      org.name.toLowerCase().includes(orgQuery.toLowerCase())
    );
  }, [organizations, orgQuery]);

  // Ügyfél kiválasztása után automatikus adatkitöltés
  const selectedCustomerId = watch('customerId');
  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);

  useEffect(() => {
    if (selectedCustomer && selectedCustomer.type === 'organization') {
      // Cég adatok kitöltése
      setValue('companyName', selectedCustomer.companyName || '');
      setValue('taxNumber', selectedCustomer.taxNumber || '');
      setValue('registrationNumber', selectedCustomer.registrationNumber || '');
      
      // Cím kitöltése - Javítva a companyAddress közvetlen használatával
      if (selectedCustomer.companyAddress) {
        // Ha van közvetlen companyAddress adat, használjuk azt
        setValue('companyAddress', selectedCustomer.companyAddress);
      } else if (selectedCustomer.address) {
        // Különben megpróbáljuk összeállítani az address komponensekből
        const addressParts = [];
        if (selectedCustomer.address.street) addressParts.push(selectedCustomer.address.street);
        if (selectedCustomer.address.city) addressParts.push(selectedCustomer.address.city);
        if (selectedCustomer.address.postalCode) addressParts.push(selectedCustomer.address.postalCode);
        if (selectedCustomer.address.country) addressParts.push(selectedCustomer.address.country);
        
        const fullAddress = addressParts.join(', ');
        setValue('companyAddress', fullAddress);
      }
      
      // Képviselő nevének kitöltése
      if (selectedCustomer.representativeName) {
        setValue('representativeName', selectedCustomer.representativeName);
        console.log('Setting representative name:', selectedCustomer.representativeName);
      } else {
        setValue('representativeName', '');
      }
      
      // Kapcsolattartó kitöltése
      if (selectedCustomer.contactPerson && selectedCustomer.contactPerson.name) {
        setValue('contactName', selectedCustomer.contactPerson.name);
        console.log('Setting contact person name:', selectedCustomer.contactPerson.name);
      } else {
        setValue('contactName', '');
      }
      
      // KÁT adatok kitöltése
      if (selectedCustomer.kat) {
        setValue('kat.kat', selectedCustomer.kat.kat || '');
        setValue('kat.text', selectedCustomer.kat.text || '');
      }
      
      // POD adatok kitöltése - Javítva a típus definícióknak megfelelően
      if (selectedCustomer.pod) {
        // Fő POD kitöltése
        setValue('pod.id', selectedCustomer.pod.id || '');
        setValue('pod.address', selectedCustomer.pod.address || '');
        setValue('pod.postalCode', selectedCustomer.pod.postalCode || '');
        setValue('pod.city', selectedCustomer.pod.city || '');
        setValue('pod.street', selectedCustomer.pod.street || '');
        setValue('pod.measurementType', selectedCustomer.pod.measurementType || '');
        setValue('pod.meteringType', selectedCustomer.pod.meteringType || ''); // AMR/SLP
        setValue('pod.annualConsumption', selectedCustomer.pod.annualConsumption || '');
        setValue('pod.power', ''); // Üres érték, mivel a Customer típusban nincs ilyen mező
        setValue('pod.profileNumber', ''); // Üres érték, mivel a Customer típusban nincs ilyen mező
      }
      
      // További POD-ok hozzáadása
      if (selectedCustomer.additionalPods && selectedCustomer.additionalPods.length > 0) {
        const newPods = selectedCustomer.additionalPods.map(pod => ({
          id: pod.id || '',
          address: pod.address || '',
          postalCode: pod.postalCode || '',
          city: pod.city || '',
          street: pod.street || '',
          measurementType: pod.measurementType || '',
          meteringType: pod.meteringType || '', // AMR/SLP
          annualConsumption: pod.annualConsumption || '',
          power: '', // Üres érték, mivel a Customer típusban nincs ilyen mező
          profileNumber: '' // Üres érték, mivel a Customer típusban nincs ilyen mező
        }));
        
        setValue('additionalPods', newPods);
        
        // POD indexek beállítása
        const podIndexes = newPods.map((_, index) => index);
        setAdditionalPods(podIndexes);
      }
    }
  }, [selectedCustomerId, selectedCustomer, quotations, setValue]);

  // Ügyfél kiválasztásának kezelése
  const handleCustomerSelect = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    console.log('Selected customer:', customer);
    
    // Többi mező beállítása
    setValue('customerId', customerId);
    
    // Ellenőrizzük, hogy van-e már kiválasztott ügyfél korábban
    const previousCustomerId = watch('customerId');
    
    // Ellenőrizzük, hogy az ajánlati szám kézzel módosított-e
    // Minta számokat az alapértelmezett generátor hoz létre, ezek frissíthetők
    const currentNumber = watch('number');
    const currentQuotationNumber = watch('quotationNumber');
    const isManuallyModified = (currentNumber && !currentNumber.includes('-Minta')) || 
                               (currentQuotationNumber && !currentQuotationNumber.includes('-Minta'));
    
    if (previousCustomerId && previousCustomerId !== customerId) {
      // Ha már volt korábban kiválasztott ügyfél, de most másikat választottunk
      if (isManuallyModified) {
        // Ha már kézzel módosítottuk az ajánlati számot, megtartjuk
        setIsInitialCustomerSelection(false);
        console.log('Customer changed, but keeping manually modified quotation number');
      } else {
        // Ha még nem módosítottuk kézzel, akkor frissítjük az új ügyfél adataival
        setIsInitialCustomerSelection(true);
        console.log('Customer changed, will update quotation number with new customer');
      }
    }
  };

  // Update quotation number when customer is selected, but only if there isn't already one set
  useEffect(() => {
    if (selectedCustomerId && selectedCustomer) {
      // Check if the form already has a quotation number (from a draft)
      const currentNumber = watch('number');
      const currentQuotationNumber = watch('quotationNumber');
      
      // Új ajánlati számot generálunk, ha:
      // 1. Az isInitialCustomerSelection igaz (első ügyfélválasztás vagy ügyfélváltás és még nem módosítottuk kézzel a számot)
      // VAGY
      // 2. Nincs még ajánlati szám VAGY a jelenlegi a "Minta" szót tartalmazza
      if (isInitialCustomerSelection || 
          (!currentNumber || currentNumber.includes('-Minta')) && 
          (!currentQuotationNumber || currentQuotationNumber.includes('-Minta'))) {
        
        // Frissítjük az adatbázisból a quotations listát, hogy biztos a legfrissebb adatokkal dolgozzunk
        refreshQuotations().then(() => {
          // Most számoljuk ki a legutolsó ajánlati számot a frissített quotations listából
          const lastQuotationNumber = quotations
            .map(q => q.number)
            .sort()
            .reverse()[0];
            
          console.log('Latest quotation number after refresh:', lastQuotationNumber);
          
          // Get the short name for the selected company
          const shortName = getCompanyShortName(selectedCustomerId);
          console.log('Customer selected, using company name:', shortName);
          
          // Minden elérhető ajánlatot átadunk, hogy a sorszámozás helyes legyen
          console.log('All available quotations for numbering:', quotations.length);
          
          // Get customer code for the new numbering system
          const customerCode = selectedCustomer?.customerCode;
          console.log('Customer code for numbering:', customerCode);
          
          // Generate a new number with the company short name and customer code
          const generatedNumber = generateQuotationNumber(
            lastQuotationNumber, 
            shortName, 
            quotations, 
            selectedCustomerId,
            'quotation',
            customerCode,
            customers
          );
          console.log('Generated quotation number with company name:', generatedNumber);
          
          // Update both fields to ensure consistency
          setValue('number', generatedNumber);
          setValue('quotationNumber', generatedNumber);
        }).catch(error => {
          console.error('Error refreshing quotations:', error);
        });
      } else {
        console.log('Keeping existing quotation number:', currentNumber || currentQuotationNumber);
      }
    }
  }, [selectedCustomerId, selectedCustomer, setValue, watch, isInitialCustomerSelection, refreshQuotations, quotations]);

  // Dokumentum feltöltés kezelése
  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      // Generáljunk egyedi fájlnevet az ütközések elkerülése érdekében
      const timestamp = Date.now();
      const uniqueFileName = `${timestamp}_${file.name}`;

      // Ideiglenes ID dokumentum útvonalához, amíg az árajánlat nem létezik
      const quotationId = initialData?.id || 'temp-' + timestamp;
      const path = `quotations/${quotationId}/documents/${uniqueFileName}`;

      // console.log('Uploading document to path:', path);
      const fileUrl = await uploadFile(file, path);

      const currentDocuments = watch('documents') || [];
      const newDocument = {
        id: timestamp.toString(),
        name: uniqueFileName, // Tároljuk az egyedi fájlnevet, hogy később törölni tudjuk
        originalName: file.name, // Az eredeti fájlnév megjelenítéshez
        url: fileUrl,
        uploadedAt: Timestamp.now(),
        type: 'other', // Alapértelmezett típus
        size: file.size
      };

      const updatedDocuments = [...currentDocuments, newDocument];
      setValue('documents', updatedDocuments as QuotationDocument[]);

      // Új feltöltött dokumentum megjelenítése a felhasználói felületen és a típus kiválasztás megnyitása
      setSelectedDocumentToEdit(newDocument);

      // Ha már van mentett árajánlat, azonnal mentsük a változást az adatbázisba is
      if (initialData?.id) {
        try {
          // Frissítsük az adatbázisban tárolt dokumentumokat
          const quotationsRef = doc(db, 'quotations', initialData.id);
          await updateDoc(quotationsRef, {
            documents: updatedDocuments,
            updatedAt: serverTimestamp()
          });
          // console.log('Document reference added to database');
        } catch (dbError) {
          console.error('Database update error:', dbError);
        }
      }
    } catch (error) {
      console.error('Error uploading document:', error);
    } finally {
      setIsUploading(false);
      // Reset input field
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Dokumentum törlés kezelése
  const handleDocumentDelete = async (documentId: string) => {
    const documents = watch('documents') || [];
    const documentToDelete = documents.find(doc => doc.id === documentId);

    if (!documentToDelete) return;

    try {
      // Frissítsük először a form állapotát, hogy a felhasználó azonnal visszajelzést kapjon
      const updatedDocuments = documents.filter(doc => doc.id !== documentId);
      setValue('documents', updatedDocuments);

      // Ha már van mentett árajánlat, azonnal mentsük a változást az adatbázisba is
      if (initialData?.id) {
        try {
          // Frissítsük az adatbázisban tárolt dokumentumokat
          const quotationsRef = doc(db, 'quotations', initialData.id);
          await updateDoc(quotationsRef, {
            documents: updatedDocuments,
            updatedAt: serverTimestamp()
          });
          // console.log('Document reference removed from database');
        } catch (dbError) {
          console.error('Database update error:', dbError);
        }
      }

      // Aztán próbáljuk törölni a fájlt a háttérben
      if (documentToDelete.url) {
        try {
          // Ha van valódi árajánlat ID
          if (initialData?.id) {
            // A feltöltési útvonalat a dokumentum nevéből vesszük, ami már tartalmazza a timestamp-et
            const path = `quotations/${initialData.id}/documents/${documentToDelete.name}`;
            // console.log('Deleting document from path:', path);
            await deleteFile(path);
          } else {
            // Ideiglenes árajánlatnál az ideiglenes ID-t használjuk, ami a feltöltéskor létrejött
            // Az ID általában a timestamp részéből jön a névnek
            const tempPath = `quotations/temp-${documentToDelete.id}/documents/${documentToDelete.name}`;
            // console.log('Deleting temp document from path:', tempPath);
            await deleteFile(tempPath);
          }
        } catch (storageError) {
          console.error('Storage delete error:', storageError);
          // Ez a hiba nem akadályozza meg a dokumentum törléséét a form-ból,
          // mert már korábban frissítettük az állapotot
        }
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      // Ha a form állapot frissítése sikertelen, próbáljunk visszaállni
      setValue('documents', documents);
    }
  };

  // Dokumentum típus változtatása
  const handleDocumentTypeChange = (documentId: string, type: string) => {
    const documents = watch('documents') || [];
    const updatedDocuments = documents.map(doc =>
      doc.id === documentId ? { ...doc, type } : doc
    );

    setValue('documents', updatedDocuments as QuotationDocument[]);

    // Ha már van mentett árajánlat, frissítsük az adatbázist is
    if (initialData?.id) {
      try {
        const quotationsRef = doc(db, 'quotations', initialData.id);
        updateDoc(quotationsRef, {
          documents: updatedDocuments,
          updatedAt: serverTimestamp()
        });
        console.log('Document type updated in database');
      } catch (error) {
        console.error('Error updating document type:', error);
      }
    }

    // Bezárjuk a dokumentum típus választó modált
    setSelectedDocumentToEdit(null);
  };

  // POD mezők hozzáadása
  const handleAddPod = () => {
    const newPods = [...additionalPods, additionalPods.length];
    setAdditionalPods(newPods);

    // Új POD adat hozzáadása a form adatokhoz
    const currentAdditionalPods = watch('additionalPods') || [];
    setValue('additionalPods', [
      ...currentAdditionalPods,
      {
        id: '',
        address: '',
        postalCode: '',
        city: '',
        street: '',
        measurementType: '',
        meteringType: '', // AMR vagy SLP
        annualConsumption: '',
        power: '',
        profileNumber: ''
      }
    ]);
  };

  // POD mező törlése
  const handleRemovePod = (index: number) => {
    const newPods = additionalPods.filter((_, i) => i !== index);
    setAdditionalPods(newPods);

    // A megfelelő elem törlése a form adatokból
    const currentAdditionalPods = watch('additionalPods') || [];
    const updatedPods = currentAdditionalPods.filter((_, i) => i !== index);
    setValue('additionalPods', updatedPods);
  };

  const handleDocumentClick = (e: React.MouseEvent<HTMLButtonElement>, document: any) => {
    e.preventDefault();
    e.stopPropagation();
    // Átalakítjuk a dokumentumot a megfelelő formátumra
    const previewDoc = {
      ...document,
      fileUrl: document.url,
      name: document.originalName || document.name
    };
    setPreviewDocument(previewDoc);
    setIsPreviewOpen(true);
  };

  // Dátum mezők kezelése
  const handleDateChange = (field: 'date' | 'contractStart' | 'contractEnd', value: string) => {
    if (value) {
      // Az érték már yyyy-MM-dd formátumban van, egyszerűen beállítjuk
      setValue(field, value);
    } else {
      // Ha törölték a dátumot
      setValue(field, '');
    }
  };

  // Dátum és idő mező kezelése (validUntil)
  const handleDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Az ISO formátumot tároljuk az eredeti formában
    setValue('validUntil', value);
    
    // Log the formatted date for debugging
    console.log('Ajánlat érvényessége beállítva:', value);
    console.log('Formázott érték:', formatDate(value));
  };

  // Inicializálás során is állítsuk be a quotationNumber értékét
  useEffect(() => {
    if (isOpen && !initialData) {
      // Ha új ajánlat készül, akkor a generált számot beállítjuk a quotationNumber mezőbe is
      setValue('quotationNumber', watch('number'));
    }
  }, [isOpen, initialData, setValue, watch]);

  // Handling tax number and registration number input with formatting
  const handleTaxNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatTaxNumber(e.target.value);
    setValue('taxNumber', formattedValue);
  };

  const handleRegistrationNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatRegistrationNumber(e.target.value);
    setValue('registrationNumber', formattedValue);
  };

  // POD kezelő függvények
  const formatPodId = (value: string): string => {
    // Eltávolítjuk az elválasztójeleket
    const cleanValue = value.replace(/-/g, '');
    // Három karakterenként formázzuk a szöveget
    let formattedValue = '';
    for (let i = 0; i < cleanValue.length; i++) {
      if (i > 0 && i % 3 === 0) {
        formattedValue += '-';
      }
      formattedValue += cleanValue[i];
    }
    return formattedValue;
  };

  // A POD ID tényleges hosszának számítása (elválasztójelek nélkül)
  const calculatePodIdLength = (value: string): number => {
    return value.replace(/-/g, '').length;
  };

  // POD mező változás kezelése
  const handlePodIdChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const { value } = e.target;
    const formattedValue = formatPodId(value);
    
    // Beállítjuk a formázott értéket
    // A fieldName biztosan létezik, így típuskényszerítéssel megoldjuk a típushibát
    setValue(fieldName as any, formattedValue);
  };

  // Fogyasztás mező formázása ezres tagolással
  const formatConsumption = (value: string | number): string => {
    if (!value) return '';
    
    // Ha szám típusú, alakítsuk át stringgé
    const stringValue = value.toString();
    
    // Eltávolítjuk a nem-szám karaktereket, kivéve a tizedespontot
    const cleanValue = stringValue.replace(/[^0-9.]/g, '');
    
    // Szétválasztjuk a tizedes rész előtti és utáni részre
    const parts = cleanValue.split('.');
    
    // Formázzuk az egész részt ezres tagolással
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    
    // Összerakjuk az eredményt
    return parts.length > 1 ? parts.join('.') : parts[0];
  };

  // Általános fogyasztás mező változáskezelés
  const handleConsumptionFieldChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const { value } = e.target;
    const originalValue = value.replace(/\s/g, ''); // Eltávolítjuk a szóközöket
    
    // Beállítjuk az eredeti értéket (szóközök nélkül)
    setValue(fieldName as any, originalValue);
    
    // Frissítjük a megjelenített értéket formázva
    e.target.value = formatConsumption(originalValue);
  };

  // Fogyasztás mező változáskezelése
  const handleConsumptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleConsumptionFieldChange(e, 'consumption');
  };

  // Clear draft when form is submitted
  const handleFormSubmit = (data: CreateQuotationData) => {
    // Készítünk egy másolatot az adatokról, hogy ne módosítsuk közvetlenül az eredeti adatokat
    const submissionData = { ...data };
    
    // Make sure representativeName is included
    submissionData.representativeName = data.representativeName;
    
    // Eltávolítjuk a formázást (ezres tagolást) az összes fogyasztás-jellegű mezőből
    if (submissionData.consumption) {
      submissionData.consumption = submissionData.consumption.toString().replace(/\s/g, '');
    }
    
    if (submissionData.price) {
      submissionData.price = submissionData.price.toString().replace(/\s/g, '');
    }
    
    if (submissionData.pod?.annualConsumption) {
      submissionData.pod.annualConsumption = submissionData.pod.annualConsumption.toString().replace(/\s/g, '');
    }
    
    if (submissionData.pod?.power) {
      submissionData.pod.power = submissionData.pod.power.toString().replace(/\s/g, '');
    }
    
    // POD mérőállások formázásának eltávolítása
    if (submissionData.additionalPods && submissionData.additionalPods.length > 0) {
      submissionData.additionalPods = submissionData.additionalPods.map(pod => ({
        ...pod,
        annualConsumption: pod.annualConsumption ? pod.annualConsumption.toString().replace(/\s/g, '') : '',
        power: pod.power ? pod.power.toString().replace(/\s/g, '') : ''
      }));
    }
    
    clearDraft();
    onSubmit(submissionData);
  };

  // Handle modal close
  const handleModalClose = () => {
    // Save draft if this is a new quotation and has meaningful data
    if (!initialData) {
      const currentValues = getValues();
      // Make sure the quotation number is preserved in the draft
      if (currentValues.number || currentValues.quotationNumber) {
        // Ensure both number fields are filled before saving
        if (currentValues.number && !currentValues.quotationNumber) {
          currentValues.quotationNumber = currentValues.number;
        } else if (currentValues.quotationNumber && !currentValues.number) {
          currentValues.number = currentValues.quotationNumber;
        }
        
        // If the user has entered any meaningful data, save the draft
        if (currentValues.organizationId || currentValues.customerId || currentValues.companyName) {
          console.log('Saving quotation draft with number:', currentValues.number);
          saveDraft(currentValues);
        }
      }
    }
    
    onCancel();
    reset();
    // Visszaállítjuk az alapértelmezett értéket, hogy a következő nyitáskor megfelelően működjön
    setIsInitialCustomerSelection(true);
  };

  // Formázott értékek beállítása a mezőkben
  React.useEffect(() => {
    // Biztosítsuk, hogy csak a form betöltése után futtassuk
    const formatAllConsumptionFields = () => {
      // Fogyasztási mezők formázása
      const consumptionValue = watch('consumption');
      const priceValue = watch('price');
      const podConsumptionValue = watch('pod.annualConsumption');
      const podPowerValue = watch('pod.power');
      
      // Csak akkor formázzuk, ha már van érték
      if (consumptionValue) {
        const formattedConsumption = formatConsumption(consumptionValue);
        const consumptionInput = document.querySelector('input[name="consumption"]') as HTMLInputElement;
        if (consumptionInput) consumptionInput.value = formattedConsumption;
      }
      
      if (priceValue) {
        const formattedPrice = formatConsumption(priceValue);
        const priceInput = document.querySelector('input[name="price"]') as HTMLInputElement;
        if (priceInput) priceInput.value = formattedPrice;
      }
      
      if (podConsumptionValue) {
        const formattedPodConsumption = formatConsumption(podConsumptionValue);
        const podConsumptionInput = document.querySelector('input[name="pod.annualConsumption"]') as HTMLInputElement;
        if (podConsumptionInput) podConsumptionInput.value = formattedPodConsumption;
      }
      
      if (podPowerValue) {
        const formattedPodPower = formatConsumption(podPowerValue);
        const podPowerInput = document.querySelector('input[name="pod.power"]') as HTMLInputElement;
        if (podPowerInput) podPowerInput.value = formattedPodPower;
      }
      
      // Additional PODs formázása
      additionalPods.forEach((podIndex) => {
        const podValue = watch(`additionalPods.${podIndex}.annualConsumption`);
        if (podValue) {
          const formattedPodValue = formatConsumption(podValue);
          const podInput = document.querySelector(`input[name="additionalPods.${podIndex}.annualConsumption"]`) as HTMLInputElement;
          if (podInput) podInput.value = formattedPodValue;
        }
        
        const podPowerValue = watch(`additionalPods.${podIndex}.power`);
        if (podPowerValue) {
          const formattedPodPowerValue = formatConsumption(podPowerValue);
          const podPowerInput = document.querySelector(`input[name="additionalPods.${podIndex}.power"]`) as HTMLInputElement;
          if (podPowerInput) podPowerInput.value = formattedPodPowerValue;
        }
      });
    };
    
    // A form betöltése után kis késleltetéssel formázzuk az értékeket
    const timeoutId = setTimeout(() => {
      formatAllConsumptionFields();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [isOpen, initialData, additionalPods]); // Csak a form megnyitásakor és a initialData változásakor futtatjuk

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      title={initialData ? t('quotations.form.title.edit') : t('quotations.form.title.new')}
      size="6xl"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6 p-6">
        {/* Draft Recovery Banner */}
        <DraftRecoveryBanner />
        
        {/* Ajánlati szám előnézet */}
        <div className="bg-emerald-100 p-4 rounded-lg border border-emerald-300 mb-4 flex items-center">
          <div className="bg-emerald-200 p-2 rounded-lg mr-3">
            <DocumentTextIcon className="h-6 w-6 text-emerald-700" />
          </div>
          <div>
            <div className="text-xs text-emerald-700 font-medium mb-1">Árajánlat azonosító</div>
            <div className="text-lg font-bold text-gray-800">{watch('quotationNumber') || watch('number')}</div>
          </div>
          <div className="ml-auto text-xs text-emerald-700 bg-emerald-200 p-2 rounded-lg">
            {!selectedCustomer ? 'Az azonosító frissül az ügyfél kiválasztásakor' : 
              isInitialCustomerSelection ? 'Ügyfél alapján generált azonosító' : 
              'Manuálisan módosított azonosító (ügyfélváltáskor megőrizve)'}
          </div>
        </div>

        {/* Basic Info Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Left Column - Organization */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('quotations.form.fields.organization')} <span className="text-red-500">*</span>
              </label>
              <Combobox
                value={watch('organizationId')}
                onChange={value => setValue('organizationId', value)}
              >
                <div className="relative">
                  <div className="relative w-full">
                    <Combobox.Input
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 
                               placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                      onChange={event => setOrgQuery(event.target.value)}
                      onClick={() => {
                        // Üres keresésre minden szervezetet mutassunk
                        setOrgQuery('');
                      }}
                      displayValue={(orgId: string) => {
                        const org = organizations.find(o => o.id === orgId);
                        return org?.name || '';
                      }}
                      placeholder={t('common.placeholders.selectOrg')}
                    />
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon
                        className="h-5 w-5 text-gray-500"
                        aria-hidden="true"
                      />
                    </Combobox.Button>
                  </div>
                  <Combobox.Options
                    className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg 
                             ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                  >
                    {filteredOrganizations.length === 0 && orgQuery !== '' ? (
                      <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                        {t('common.placeholders.selectOrg')}
                      </div>
                    ) : (
                      filteredOrganizations.map(org => (
                        <Combobox.Option
                          key={org.id}
                          value={org.id}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-primary-100 text-primary-900' : 'text-gray-800'
                            }`
                          }
                        >
                          {({ selected, active }) => (
                            <div className="flex items-center space-x-3">
                              <div className="p-1 rounded bg-blue-100">
                                <BuildingOffice2Icon className="h-4 w-4 text-blue-600" />
                              </div>
                              <div>
                                <div className="font-medium">{org.name}</div>
                                <div className="text-xs text-gray-500">
                                  {org.address?.city}, {org.address?.country}
                                </div>
                              </div>
                            </div>
                          )}
                        </Combobox.Option>
                      ))
                    )}
                  </Combobox.Options>
                </div>
              </Combobox>
            </div>

            {selectedOrg && (
              <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className="p-2 rounded bg-blue-100">
                    <BuildingOffice2Icon className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-800 mb-1">
                      {selectedOrg.name}
                    </h4>

                    {/* Tax Number */}
                    {selectedOrg.taxNumber && (
                      <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2">
                        <IdentificationIcon className="h-4 w-4" />
                        <span>{selectedOrg.taxNumber}</span>
                      </div>
                    )}

                    {/* Address */}
                    {selectedOrg.address && (
                      <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2">
                        <MapPinIcon className="h-4 w-4 shrink-0" />
                        <span className="truncate">
                          {selectedOrg.address.street}, {selectedOrg.address.city}, {selectedOrg.address.postalCode}, {selectedOrg.address.country}
                        </span>
                      </div>
                    )}

                    {/* Contact Info */}
                    <div className="flex flex-wrap gap-3">
                      {selectedOrg.contact?.email && (
                        <a
                          href={`mailto:${selectedOrg.contact.email}`}
                          className="flex items-center space-x-1 text-xs text-gray-600 hover:text-primary-600"
                        >
                          <EnvelopeIcon className="h-4 w-4" />
                          <span>{selectedOrg.contact.email}</span>
                        </a>
                      )}
                      {selectedOrg.contact?.phone && (
                        <a
                          href={`tel:${selectedOrg.contact.phone}`}
                          className="flex items-center space-x-1 text-xs text-gray-600 hover:text-primary-600"
                        >
                          <PhoneIcon className="h-4 w-4" />
                          <span>{selectedOrg.contact.phone}</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Customer */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('quotations.form.fields.customer')} <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                <Combobox
                  value={watch('customerId')}
                  onChange={(customerId) => handleCustomerSelect(customerId)}
                >
                  <div className="relative">
                    <div className="relative w-full">
                      <Combobox.Input
                        className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 
                                placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        onChange={event => setCustomerQuery(event.target.value)}
                        onClick={() => {
                          // Üres keresésre minden ügyfelet mutassunk
                          setCustomerQuery('');
                        }}
                        displayValue={(customerId: string) => {
                          const customer = customers.find(c => c.id === customerId);
                          if (!customer) return '';
                          return customer.type === 'organization'
                            ? customer.companyName || ''
                            : `${customer.firstName} ${customer.lastName}`;
                        }}
                        placeholder={t('common.placeholders.selectCustomer')}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon
                          className="h-5 w-5 text-gray-500"
                          aria-hidden="true"
                        />
                      </Combobox.Button>
                    </div>
                    <Combobox.Options
                      className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg 
                              ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                    >
                      {filteredCustomers.length === 0 && customerQuery !== '' ? (
                        <div className="relative cursor-default select-none py-2 px-4 text-gray-500">
                          {t('common.placeholders.selectCustomer')}
                        </div>
                      ) : (
                        filteredCustomers.map(customer => (
                          <Combobox.Option
                            key={customer.id}
                            value={customer.id}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-primary-100 text-primary-900' : 'text-gray-800'
                              }`
                            }
                          >
                            {({ selected, active }) => (
                              <div className="flex items-center space-x-3">
                                <div className={`p-1 rounded ${customer.type === 'organization'
                                  ? 'bg-blue-100'
                                  : 'bg-purple-100'
                                  }`}>
                                  {customer.type === 'organization' ? (
                                    <BuildingOffice2Icon className="h-4 w-4 text-blue-600" />
                                  ) : (
                                    <UserIcon className="h-4 w-4 text-purple-600" />
                                  )}
                                </div>
                                <div>
                                  <div className="font-medium">
                                    {customer.type === 'organization'
                                      ? customer.companyName
                                      : `${customer.firstName} ${customer.lastName}`}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {customer.email}
                                  </div>
                                </div>
                              </div>
                            )}
                          </Combobox.Option>
                        ))
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>

                {/* Új ügyfél létrehozása gomb */}
                <button
                  type="button"
                  onClick={() => setShowNewCustomerModal(true)}
                  className="flex items-center justify-center w-full px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-md transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('quotations.form.buttons.createNewCustomer') || 'Új ügyfél létrehozása'}
                </button>
              </div>
            </div>

            {selectedCustomer && (
              <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded ${selectedCustomer.type === 'organization'
                    ? 'bg-blue-100'
                    : 'bg-purple-100'
                    }`}>
                    {selectedCustomer.type === 'organization' ? (
                      <BuildingOffice2Icon className="h-5 w-5 text-blue-600" />
                    ) : (
                      <UserIcon className="h-5 w-5 text-purple-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {/* Name/Company */}
                    <h4 className="text-sm font-medium text-gray-800 mb-1">
                      {selectedCustomer.type === 'organization'
                        ? selectedCustomer.companyName
                        : `${selectedCustomer.firstName} ${selectedCustomer.lastName}`}
                    </h4>

                    {/* Tax Number - csak szervezetnél */}
                    {selectedCustomer.type === 'organization' && selectedCustomer.taxNumber && (
                      <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2">
                        <IdentificationIcon className="h-4 w-4" />
                        <span>{selectedCustomer.taxNumber}</span>
                      </div>
                    )}

                    {/* Address */}
                    {selectedCustomer.address && (
                      <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2">
                        <MapPinIcon className="h-4 w-4 shrink-0" />
                        <span className="truncate">
                          {selectedCustomer.companyAddress || 
                            [
                              selectedCustomer.address.street, 
                              selectedCustomer.address.city,
                              selectedCustomer.address.postalCode, 
                              selectedCustomer.address.country
                            ].filter(Boolean).join(', ')}
                        </span>
                      </div>
                    )}

                    {/* Representative Name */}
                    {selectedCustomer.representativeName && (
                      <div className="flex items-center space-x-2 text-xs text-gray-600 mb-2">
                        <UserIcon className="h-4 w-4 shrink-0" />
                        <span className="truncate">
                          <span className="font-medium">Képviselő:</span> {selectedCustomer.representativeName}
                        </span>
                      </div>
                    )}

                    {/* Contact Info */}
                    <div className="flex flex-wrap gap-3">
                      {selectedCustomer.email && (
                        <a
                          href={`mailto:${selectedCustomer.email}`}
                          className="flex items-center space-x-1 text-xs text-gray-600 hover:text-primary-600"
                        >
                          <EnvelopeIcon className="h-4 w-4" />
                          <span>{selectedCustomer.email}</span>
                        </a>
                      )}
                      {selectedCustomer.phone && (
                        <a
                          href={`tel:${selectedCustomer.phone}`}
                          className="flex items-center space-x-1 text-xs text-gray-600 hover:text-primary-600"
                        >
                          <PhoneIcon className="h-4 w-4" />
                          <span>{selectedCustomer.phone}</span>
                        </a>
                      )}
                    </div>

                    {/* Contact Person - csak szervezetnél */}
                    {selectedCustomer.type === 'organization' && selectedCustomer.contactPerson && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="text-xs font-medium text-gray-600 mb-2">
                          {t('quotations.form.fields.contactPerson')}
                        </div>
                        <div className="space-y-1">
                          <div className="text-sm text-gray-800">
                            {selectedCustomer.contactPerson.name}
                            {selectedCustomer.contactPerson.position && (
                              <span className="text-gray-500 text-xs ml-2">
                                ({selectedCustomer.contactPerson.position})
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-3">
                            {selectedCustomer.contactPerson.email && (
                              <a
                                href={`mailto:${selectedCustomer.contactPerson.email}`}
                                className="flex items-center space-x-1 text-xs text-gray-600 hover:text-primary-600"
                              >
                                <EnvelopeIcon className="h-4 w-4" />
                                <span>{selectedCustomer.contactPerson.email}</span>
                              </a>
                            )}
                            {selectedCustomer.contactPerson.phone && (
                              <a
                                href={`tel:${selectedCustomer.contactPerson.phone}`}
                                className="flex items-center space-x-1 text-xs text-gray-600 hover:text-primary-600"
                              >
                                <PhoneIcon className="h-4 w-4" />
                                <span>{selectedCustomer.contactPerson.phone}</span>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Company Details Section */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-md mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-blue-300 to-blue-400 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <BuildingOffice2Icon className="w-5 h-5 text-blue-700" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium text-gray-800">
                {t('quotations.form.sections.companyDetails.title')}
              </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.companyDetails.quotationNumber')}
                  </label>
                  <input
                    type="text"
                    {...register('quotationNumber')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.companyDetails.companyName')}
                  </label>
                  <input
                    type="text"
                    {...register('companyName')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.companyDetails.companyAddress')}
                  </label>
                  <input
                    type="text"
                    {...register('companyAddress')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('quotations.form.sections.companyDetails.taxNumber')}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={watch('taxNumber') || ''}
                    onChange={handleTaxNumberChange}
                      placeholder="12345678-1-23"
                      className={`w-full bg-gray-50 border ${
                        watch('taxNumber') && !validateTaxNumber(watch('taxNumber') || '') 
                        ? 'border-red-500' 
                        : 'border-gray-300'
                      } rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500`}
                    />
                    {watch('taxNumber') && !validateTaxNumber(watch('taxNumber') || '') && (
                      <div className="absolute right-2 top-2 text-red-500">
                        <ExclamationTriangleIcon className="h-4 w-4" />
                      </div>
                    )}
                    {watch('taxNumber') && !validateTaxNumber(watch('taxNumber') || '') && (
                      <div className="text-xs mt-1 text-red-500">
                        {t('quotations.form.validation.taxNumber')}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">
                    {t('quotations.form.sections.companyDetails.registrationNumber')}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={watch('registrationNumber') || ''}
                    onChange={handleRegistrationNumberChange}
                      placeholder="01-10-123456"
                      className={`w-full bg-gray-50 border ${
                        watch('registrationNumber') && !validateRegistrationNumber(watch('registrationNumber') || '') 
                        ? 'border-red-500' 
                        : 'border-gray-300'
                      } rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500`}
                    />
                    {watch('registrationNumber') && !validateRegistrationNumber(watch('registrationNumber') || '') && (
                      <div className="absolute right-2 top-2 text-red-500">
                        <ExclamationTriangleIcon className="h-4 w-4" />
                </div>
                    )}
                    {watch('registrationNumber') && !validateRegistrationNumber(watch('registrationNumber') || '') && (
                      <div className="text-xs mt-1 text-red-500">
                        {t('quotations.form.validation.registrationNumber')}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 mt-4">
                    {t('customers.form.contactDetails.representativeName') || "Képviselő neve"}
                  </label>
                  <input
                    type="text"
                    {...register('representativeName')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Contact & Pricing Section */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-md">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-green-300 to-teal-400 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <CurrencyDollarIcon className="w-5 h-5 text-green-700" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <h3 className="text-sm font-medium text-gray-800">
                {t('quotations.form.sections.contactAndPricing.title')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('customers.form.contactDetails.representativeName') || "Képviselő neve"}
                  </label>
                  <input
                    type="text"
                    {...register('representativeName')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.contactAndPricing.contactName')}
                  </label>
                  <input
                    type="text"
                    {...register('contactName')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.contactAndPricing.date')}
                  </label>
                  <DatePicker
                    selected={watch('date') ? new Date(watch('date')) : null}
                    onChange={(date) => handleDateChange('date', date ? date.toISOString().split('T')[0] : '')}
                    className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.contactAndPricing.validUntil')}
                  </label>
                  <div className="relative">
                    <input
                      type="datetime-local"
                      value={watch('validUntil') || ''}
                      onChange={handleDateTimeChange}
                      className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {watch('validUntil') && (
                      <div className="text-xs mt-1 text-gray-600">
                        Formázott: {formatDate(watch('validUntil'))}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.contactAndPricing.paymentDue')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    {...register('paymentDue')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('quotations.form.sections.contactAndPricing.paymentDuePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.contactAndPricing.price')}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    {...register('price')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    onChange={(e) => handleConsumptionFieldChange(e, 'price')}
                    onFocus={(e) => {
                      // Fókusz esetén az eredeti értéket mutatjuk (formázás nélkül)
                      e.target.value = watch('price') || '';
                    }}
                    onBlur={(e) => {
                      // Fókusz elvesztésekor újra formázzuk
                      e.target.value = formatConsumption(watch('price') || '');
                    }}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.contactAndPricing.priceUnit')}
                  </label>
                  <select
                    {...register('priceUnit')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="HUF/kWh">HUF/kWh</option>
                    <option value="EUR/MWh">EUR/MWh</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Consumption and Contract Section */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-md mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-indigo-300 to-indigo-400 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <AdjustmentsHorizontalIcon className="w-5 h-5 text-indigo-700" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium text-gray-800">
                  {t('quotations.form.sections.consumptionAndContract.title')}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.consumptionAndContract.consumption')}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    {...register('consumption')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    onChange={handleConsumptionChange}
                    onFocus={(e) => {
                      // Fókusz esetén az eredeti értéket mutatjuk (formázás nélkül)
                      e.target.value = watch('consumption') || '';
                    }}
                    onBlur={(e) => {
                      // Fókusz elvesztésekor újra formázzuk
                      e.target.value = formatConsumption(watch('consumption') || '');
                    }}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.consumptionAndContract.contractStart')}
                  </label>
                  <DatePicker
                    selected={watch('contractStart') ? new Date(watch('contractStart')) : null}
                    onChange={(date) => handleDateChange('contractStart', date ? date.toISOString().split('T')[0] : '')}
                    className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.consumptionAndContract.contractEnd')}
                  </label>
                  <DatePicker
                    selected={watch('contractEnd') ? new Date(watch('contractEnd')) : null}
                    onChange={(date) => handleDateChange('contractEnd', date ? date.toISOString().split('T')[0] : '')}
                    className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* KÁT Section */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-md mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-rose-300 to-pink-400 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <DocumentTextIcon className="w-5 h-5 text-rose-700" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium text-gray-800">
                  {t('quotations.form.sections.kat.title')}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('customers.form.kat.kat')}
                  </label>
                  <input
                    type="text"
                    {...register('kat.kat')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 
                             placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.kat.text')}
                  </label>
                  <textarea
                    {...register('kat.text')}
                    rows={3}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* POD Section */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-md mb-4">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-amber-300 to-orange-400 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <MapPinIcon className="w-5 h-5 text-amber-700" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium text-gray-800">
                  {t('quotations.form.sections.pod.title')}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.pod.podId')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                  <input
                    type="text"
                    {...register('pod.id')}
                    maxLength={33}
                    className={`w-full bg-gray-50 border ${
                      watch('pod.id')?.length !== 33 && watch('pod.id')?.length > 0 
                        ? 'border-red-500' 
                        : 'border-gray-300'
                    } rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500`}
                  />
                    {watch('pod.id')?.length > 0 && watch('pod.id')?.length !== 33 && (
                      <div className="absolute right-2 top-2 text-red-500">
                        <ExclamationTriangleIcon className="h-4 w-4" />
                      </div>
                    )}
                    {watch('pod.id')?.length > 0 && (
                      <div className="text-xs mt-1 text-gray-400">
                        {watch('pod.id')?.length}/33 karakter
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.pod.podAddress')}
                  </label>
                  <input
                    type="text"
                    {...register('pod.address')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.pod.measurementType')}
                  </label>
                  <select
                    {...register('pod.measurementType')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Válasszon...</option>
                    <optgroup label="AMR (Negyedórás)">
                      <option value="negyedoras">AMR - Negyedórás (15 perc)</option>
                      <option value="negyedoras_hmke">AMR - Negyedórás (15 perc) - HMKE</option>
                    </optgroup>
                    <optgroup label="SLP (Profilos)">
                      <option value="profil">SLP (profil alapú)</option>
                      <option value="tavmert_slp">SLP (KIF4) - Távmért</option>
                      <option value="profil_hmke">SLP (profil alapú) - HMKE</option>
                      <option value="tavmert_slp_hmke">SLP (KIF4) - Távmért - HMKE</option>
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.pod.power')}
                  </label>
                  <Tooltip content="Ez a mező már nem használható. Kivezetésre került a rendszerből.">
                    <input
                      type="text"
                      inputMode="numeric"
                      {...register('pod.power')}
                      className="w-full bg-gray-100 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-500 placeholder-gray-400 focus:outline-none cursor-not-allowed"
                      onChange={(e) => handleConsumptionFieldChange(e, 'pod.power')}
                      onFocus={(e) => {
                        // Fókusz esetén az eredeti értéket mutatjuk (formázás nélkül)
                        e.target.value = watch('pod.power') || '';
                      }}
                      onBlur={(e) => {
                        // Fókusz elvesztésekor újra formázzuk
                        e.target.value = formatConsumption(watch('pod.power') || '');
                      }}
                      disabled={true}
                    />
                  </Tooltip>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.pod.profileNumber')}
                  </label>
                  <Tooltip content="Ez a mező már nem használható. Kivezetésre került a rendszerből.">
                    <input
                      type="text"
                      {...register('pod.profileNumber')}
                      className="w-full bg-gray-100 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-500 placeholder-gray-400 focus:outline-none cursor-not-allowed"
                      disabled={true}
                    />
                  </Tooltip>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('quotations.form.sections.pod.annualConsumption')}
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    {...register('pod.annualConsumption')}
                    className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    onChange={(e) => handleConsumptionFieldChange(e, 'pod.annualConsumption')}
                    onFocus={(e) => {
                      // Fókusz esetén az eredeti értéket mutatjuk (formázás nélkül)
                      e.target.value = watch('pod.annualConsumption') || '';
                    }}
                    onBlur={(e) => {
                      // Fókusz elvesztésekor újra formázzuk
                      e.target.value = formatConsumption(watch('pod.annualConsumption') || '');
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* További POD hozzáadása gomb */}
        <div className="flex justify-center mb-6">
          <button
            type="button"
            onClick={handleAddPod}
            className="flex items-center justify-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-md text-white text-sm transition-colors shadow-sm"
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            <span>{t('quotations.form.sections.additionalPods.title')}</span>
          </button>
        </div>

        {/* További POD szakaszok */}
                  {additionalPods.map((podIndex, index) => (
          <div key={`additional-pod-${podIndex}`} className="bg-white rounded-lg border border-gray-200 shadow-md mb-4">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-cyan-300 to-blue-400 flex items-center justify-center rounded-l-lg border-r border-gray-200">
                <MapPinIcon className="w-5 h-5 text-cyan-700" />
              </div>
              <div className="p-4 flex-1 space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-medium text-gray-800">
                    {t('quotations.form.sections.additionalPods.title', { index: index + 2 })}
                  </h3>
                  <button
                    type="button"
                    onClick={() => handleRemovePod(podIndex)}
                    className="text-gray-500 hover:text-red-600"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('quotations.form.sections.additionalPods.podId', { index: index + 2 })} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        {...register(`additionalPods.${podIndex}.id`)}
                        maxLength={33}
                        className={`w-full bg-gray-50 border ${
                          watch(`additionalPods.${podIndex}.id`)?.length !== 33 && watch(`additionalPods.${podIndex}.id`)?.length > 0 
                            ? 'border-red-500' 
                            : 'border-gray-300'
                        } rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500`}
                      />
                      {watch(`additionalPods.${podIndex}.id`)?.length > 0 && watch(`additionalPods.${podIndex}.id`)?.length !== 33 && (
                        <div className="absolute right-2 top-2 text-red-500">
                          <ExclamationTriangleIcon className="h-4 w-4" />
                        </div>
                      )}
                      {watch(`additionalPods.${podIndex}.id`)?.length > 0 && (
                        <div className="text-xs mt-1 text-gray-400">
                          {watch(`additionalPods.${podIndex}.id`)?.length}/33 karakter
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('quotations.form.sections.additionalPods.podAddress', { index: index + 2 })}
                    </label>
                    <input
                      type="text"
                      {...register(`additionalPods.${podIndex}.address`)}
                      className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('quotations.form.sections.additionalPods.measurementType', { index: index + 2 })}
                    </label>
                    <select
                      {...register(`additionalPods.${podIndex}.measurementType`)}
                      className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Válasszon...</option>
                      <optgroup label="AMR (Negyedórás)">
                        <option value="negyedoras">AMR - Negyedórás (15 perc)</option>
                        <option value="negyedoras_hmke">AMR - Negyedórás (15 perc) - HMKE</option>
                      </optgroup>
                      <optgroup label="SLP (Profilos)">
                        <option value="profil">SLP (profil alapú)</option>
                        <option value="tavmert_slp">SLP (KIF4) - Távmért</option>
                        <option value="profil_hmke">SLP (profil alapú) - HMKE</option>
                        <option value="tavmert_slp_hmke">SLP (KIF4) - Távmért - HMKE</option>
                      </optgroup>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('quotations.form.sections.additionalPods.annualConsumption', { index: index + 2 })}
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      {...register(`additionalPods.${podIndex}.annualConsumption`)}
                      className="w-full bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                      onChange={(e) => handleConsumptionFieldChange(e, `additionalPods.${podIndex}.annualConsumption`)}
                      onFocus={(e) => {
                        // Fókusz esetén az eredeti értéket mutatjuk (formázás nélkül)
                        e.target.value = watch(`additionalPods.${podIndex}.annualConsumption`) || '';
                      }}
                      onBlur={(e) => {
                        // Fókusz elvesztésekor újra formázzuk
                        e.target.value = formatConsumption(watch(`additionalPods.${podIndex}.annualConsumption`) || '');
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('quotations.form.sections.additionalPods.power', { index: index + 2 })}
                    </label>
                    <Tooltip content="Ez a mező már nem használható. Kivezetésre került a rendszerből.">
                      <input
                        type="text"
                        inputMode="numeric"
                        {...register(`additionalPods.${podIndex}.power`)}
                        className="w-full bg-gray-100 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-500 placeholder-gray-400 focus:outline-none cursor-not-allowed"
                        onChange={(e) => handleConsumptionFieldChange(e, `additionalPods.${podIndex}.power`)}
                        onFocus={(e) => {
                          // Fókusz esetén az eredeti értéket mutatjuk (formázás nélkül)
                          e.target.value = watch(`additionalPods.${podIndex}.power`) || '';
                        }}
                        onBlur={(e) => {
                          // Fókusz elvesztésekor újra formázzuk
                          e.target.value = formatConsumption(watch(`additionalPods.${podIndex}.power`) || '');
                        }}
                        disabled={true}
                      />
                    </Tooltip>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {t('quotations.form.sections.additionalPods.profileNumber', { index: index + 2 })}
                    </label>
                    <Tooltip content="Ez a mező már nem használható. Kivezetésre került a rendszerből.">
                      <input
                        type="text"
                        {...register(`additionalPods.${podIndex}.profileNumber`)}
                        className="w-full bg-gray-100 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-500 placeholder-gray-400 focus:outline-none cursor-not-allowed"
                        disabled={true}
                      />
                    </Tooltip>
                  </div>
                  
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Documents Section */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-md">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-purple-300 to-violet-400 flex items-center justify-center rounded-l-lg border-r border-gray-200">
              <DocumentIcon className="w-5 h-5 text-purple-700" />
            </div>
            <div className="p-4 flex-1 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-medium text-gray-800">
                  {t('quotations.form.sections.documents.title')}
                </h3>
              </div>

              {/* Document Upload Input */}
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 
                                rounded-md cursor-pointer transition-colors">
                  <PaperClipIcon className="w-4 h-4 text-gray-600" />
                  <span className="text-sm text-gray-700">
                    {t('quotations.form.buttons.uploadDocument')}
                  </span>
                  <input
                    type="file"
                    onChange={handleDocumentUpload}
                    disabled={isUploading}
                    className="hidden"
                  />
                </label>
                {isUploading && (
                  <span className="text-xs text-gray-400">
                    {t('common.uploading') || 'Uploading...'}
                    </span>
                  )}
                </div>

              {/* Document List */}
              <div className="space-y-2">
                {(watch('documents') || []).length === 0 ? (
                  <div className="text-sm text-gray-500 italic">
                    {t('quotations.form.messages.noDocuments')}
              </div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {(watch('documents') || []).map((document) => (
                      <li key={document.id} className="py-3 flex justify-between items-center">
                        <div className="flex items-center space-x-3">
                          {document.type === 'contract' && <DocumentCheckIcon className="w-5 h-5 text-primary-600" />}
                          {document.type === 'quotation' && <DocumentChartBarIcon className="w-5 h-5 text-orange-600" />}
                          {(!document.type || document.type === 'other') && <DocumentIcon className="w-5 h-5 text-gray-500" />}
                          <div>
                            <div className="text-sm font-medium text-gray-800">
                              {document.originalName || document.name}
            </div>
                            <div className="flex items-center text-xs">
                              <span className="text-gray-500">
                                {formatDocumentDate(document.uploadedAt)}
                              </span>
                              {document.size && (
                                <span className="text-gray-500 ml-1">({Math.round(document.size / 1024)} KB)</span>
                              )}
                              <span className="mx-1.5 text-gray-400">•</span>
                              <button
                                onClick={(e) => handleDocumentClick(e, document)}
                                className="text-primary-600 hover:text-primary-800"
                              >
                                {document.type === 'contract' && (t('customers.form.documents.typeContract') || 'Contract')}
                                {document.type === 'quotation' && (t('customers.form.documents.typeQuotation') || 'Quotation')}
                                {(!document.type || document.type === 'other') && (t('customers.form.documents.typeOther') || 'Other')}
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Tooltip content={t('quotations.form.buttons.viewDocument') || 'View'}>
                            <button
                              onClick={(e) => handleDocumentClick(e, document)}
                              className="text-primary-600 hover:text-primary-800 transition-colors"
                            >
                              <PaperClipIcon className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip content={t('quotations.form.buttons.deleteDocument') || 'Delete'}>
                            <button
                              type="button"
                              onClick={() => handleDocumentDelete(document.id)}
                              className="text-gray-500 hover:text-red-600 transition-colors"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleModalClose}
            className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-800"
          >
            {t('quotations.form.buttons.cancel')}
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
          >
            {initialData ? t('quotations.form.buttons.updateOffer') : t('quotations.form.buttons.createOffer')}
          </button>
        </div>
      </form>

      {/* Dokumentum típus választó modal */}
      <Modal
        isOpen={!!selectedDocumentToEdit}
        onClose={() => setSelectedDocumentToEdit(null)}
        title={t('customers.form.documents.selectType') || 'Select Document Type'}
        size="md"
      >
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            {t('customers.form.documents.selectTypeDescription') || 'Please select the type of this document:'}
          </p>

          <div className="grid grid-cols-1 gap-3">
            <button
              type="button"
              onClick={() => handleDocumentTypeChange(selectedDocumentToEdit?.id, 'contract')}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-300 hover:border-primary-500 hover:bg-primary-50 transition-all"
            >
              <div className="flex items-center">
                <DocumentCheckIcon className="w-5 h-5 mr-3 text-primary-600" />
                <span className="text-sm font-medium text-gray-800">
                  {t('customers.form.documents.typeContract') || 'Contract'}
                </span>
              </div>
              {selectedDocumentToEdit?.type === 'contract' && (
                <div className="w-2 h-2 rounded-full bg-primary-500"></div>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleDocumentTypeChange(selectedDocumentToEdit?.id, 'quotation')}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-300 hover:border-primary-500 hover:bg-primary-50 transition-all"
            >
              <div className="flex items-center">
                <DocumentChartBarIcon className="w-5 h-5 mr-3 text-orange-600" />
                <span className="text-sm font-medium text-gray-800">
                  {t('customers.form.documents.typeQuotation') || 'Quotation'}
                </span>
              </div>
              {selectedDocumentToEdit?.type === 'quotation' && (
                <div className="w-2 h-2 rounded-full bg-primary-500"></div>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleDocumentTypeChange(selectedDocumentToEdit?.id, 'other')}
              className="flex items-center justify-between p-3 rounded-lg border border-gray-300 hover:border-primary-500 hover:bg-primary-50 transition-all"
            >
              <div className="flex items-center">
                <DocumentIcon className="w-5 h-5 mr-3 text-gray-500" />
                <span className="text-sm font-medium text-gray-800">
                  {t('customers.form.documents.typeOther') || 'Other Document'}
                </span>
              </div>
              {selectedDocumentToEdit?.type === 'other' && (
                <div className="w-2 h-2 rounded-full bg-primary-500"></div>
              )}
            </button>
          </div>

          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={() => setSelectedDocumentToEdit(null)}
              className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
            >
              {t('common.close') || 'Close'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Dokumentum előnézet modal */}
      <DocumentPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewDocument(null);
        }}
        contract={previewDocument}
      />

      {/* Új ügyfél létrehozása modal */}
      <Modal
        isOpen={showNewCustomerModal}
        onClose={() => setShowNewCustomerModal(false)}
        title={t('customers.form.title.new') || 'Új ügyfél létrehozása'}
        size="4xl"
      >
        <div className="p-6">
          <div className="mb-6">
            <div className="flex space-x-4 mb-4">
              <button
                type="button"
                className={`flex-1 py-3 px-4 rounded-lg border ${
                  !isNewCustomerMode
                    ? 'bg-primary-50 border-primary-200 text-primary-700'
                    : 'bg-white border-gray-300 text-gray-700'
                }`}
                onClick={() => setIsNewCustomerMode(false)}
              >
                <div className="flex items-center justify-center">
                  <BuildingOffice2Icon className="h-5 w-5 mr-2 text-current" />
                  <span className="font-medium">{t('customers.form.types.organization') || 'Szervezet'}</span>
                </div>
              </button>
              
              <button
                type="button"
                className={`flex-1 py-3 px-4 rounded-lg border ${
                  isNewCustomerMode
                    ? 'bg-primary-50 border-primary-200 text-primary-700'
                    : 'bg-white border-gray-300 text-gray-700'
                }`}
                onClick={() => setIsNewCustomerMode(true)}
              >
                <div className="flex items-center justify-center">
                  <UserIcon className="h-5 w-5 mr-2 text-current" />
                  <span className="font-medium">{t('customers.form.types.individual') || 'Magánszemély'}</span>
                </div>
              </button>
            </div>
            
            <p className="text-sm text-gray-600">
              {isNewCustomerMode
                ? t('quotations.form.newCustomer.individualDescription') || 'Magánszemély ügyfél létrehozása egyszerűsített adatokkal. További adatok később is megadhatók.'
                : t('quotations.form.newCustomer.organizationDescription') || 'Szervezet létrehozása egyszerűsített adatokkal. További adatok később is megadhatók.'
              }
            </p>
          </div>
          
          {/* Itt később implementálható az egyszerűsített ügyfél űrlap */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-amber-800">{t('common.inDevelopment') || 'Fejlesztés alatt'}</h3>
                <p className="text-xs text-amber-700">
                  {t('quotations.form.newCustomer.inDevelopmentNote') || 'Ez a funkció jelenleg fejlesztés alatt áll. Kérjük, használja az Ügyfelek menüt új ügyfél létrehozásához.'}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowNewCustomerModal(false)}
              className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-800"
            >
              {t('common.cancel') || 'Mégsem'}
            </button>
            <button
              type="button"
              onClick={() => {
                // Itt később implementálható az új ügyfél mentése
                setShowNewCustomerModal(false);
              }}
              className="px-4 py-2 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md"
            >
              {t('common.create') || 'Létrehozás'}
            </button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default QuotationForm; 